import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, ClipboardList, Activity, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import GreetingCard from '@/components/dashboard/widgets/GreetingCard';
import StatTile from '@/components/dashboard/widgets/StatTile';
import SectionCard from '@/components/dashboard/widgets/SectionCard';
import TeamMembersCard from '@/components/dashboard/widgets/TeamMembersCard';
import SimulationsCard from '@/components/dashboard/widgets/SimulationsCard';
import simulationService from '@/services/simulationService';
import sessionService from '@/services/sessionService';
import teamService from '@/services/teamService';
import { useDocumentProcessingPoller } from '@/hooks/useDocumentProcessingPoller';
import type { SimulationResponse } from '@/types/simulation';
import type { TeamStatsResponse, TeamRankingsResponse, SalespersonStats } from '@/types/session';
import type { TeamResponse } from '@/services/teamService';
import type { User } from '@/types/auth';
import { toScore99 } from '@/utils/scoreFormat';

type DashboardData = {
  simulations: SimulationResponse[];
  stats: TeamStatsResponse | null;
  rankings: TeamRankingsResponse | null;
  teams: TeamResponse[];
  salespeople: User[];
};

export default function ManagerDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.allSettled([
      simulationService.getAll(),
      sessionService.getTeamStats(),
      sessionService.getTeamRankings(),
      teamService.getMyTeams(),
      teamService.getSalespeople(),
    ])
      .then(([sims, stats, rankings, teams, salespeople]) => {
        if (cancelled) return;
        setData({
          simulations: sims.status === 'fulfilled' ? sims.value : [],
          stats: stats.status === 'fulfilled' ? stats.value : null,
          rankings: rankings.status === 'fulfilled' ? rankings.value : null,
          teams: teams.status === 'fulfilled' ? teams.value : [],
          salespeople: salespeople.status === 'fulfilled' ? salespeople.value : [],
        });
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useDocumentProcessingPoller(data?.simulations, (updated) => {
    setData((prev) =>
      prev
        ? { ...prev, simulations: prev.simulations.map((s) => (s.id === updated.id ? updated : s)) }
        : prev,
    );
  });

  const totalSims = data?.simulations.length ?? 0;
  const totalSalespeople = data?.salespeople.length ?? 0;
  const totalTeams = data?.teams.length ?? 0;
  const teamSessionsCount = data?.stats?.totalSessions ?? 0;
  const avgScore = data?.stats?.averageScore ?? 0;
  const improvementPct = data?.stats?.teamImprovementPercent ?? 0;

  const monthlyGoal = 20;
  const monthlyDone = teamSessionsCount;
  const goalPct = Math.min(100, Math.round((monthlyDone / monthlyGoal) * 100));

  const statsByUserId = useMemo(() => {
    const map: Record<string, SalespersonStats> = {};
    (data?.stats?.salespersonStats ?? []).forEach((s) => {
      map[s.userId] = s;
    });
    return map;
  }, [data?.stats?.salespersonStats]);

  return (
    <div className="animate-fade-in">
      {error && (
        <div role="alert" className="mb-5 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {t('dashboard.error', 'No se pudieron cargar los datos. Intenta recargar.')}
        </div>
      )}

      {/* 2-column main grid (Agencium layout) */}
      <div className="grid gap-5 md:grid-cols-2">
        {/* LEFT COLUMN */}
        <div className="space-y-5">
          <GreetingCard
            firstName={user?.firstName}
            lastName={user?.lastName}
            subtitle={t('dashboard.manager.subtitle', 'Así va tu equipo hoy')}
          />

          <TeamMembersCard
            teams={data?.teams ?? []}
            statsByUserId={statsByUserId}
            teamRanking={data?.rankings?.teams ?? []}
            loading={loading}
          />
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-5">
          <SectionCard title={t('dashboard.goal.globalTitle', 'Objetivo mensual')}>
            {loading ? (
              <Skeleton className="h-6 w-full" />
            ) : (
              <>
                <div className="mb-3 flex items-baseline justify-between">
                  <span className="text-4xl font-semibold tabular-nums text-foreground">{goalPct}%</span>
                  <span className="text-sm text-muted-foreground">
                    {monthlyDone} / {monthlyGoal} {t('dashboard.goal.sessionsTotal', 'sesiones')}
                  </span>
                </div>
                <Progress value={goalPct} />
                {improvementPct !== 0 && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    {improvementPct > 0 ? '↑' : '↓'} {Math.abs(improvementPct).toFixed(1)}%{' '}
                    {t('dashboard.goal.improvement', 'vs mes anterior')}
                  </p>
                )}
              </>
            )}
          </SectionCard>

          <SectionCard
            title={t('dashboard.actions.title', 'Acciones importantes')}
            contentClassName="grid grid-cols-2 xl:grid-cols-4 gap-3"
          >
            <StatTile
              icon={ClipboardList}
              tone="info"
              label={t('dashboard.manager.simCount', 'Simulaciones')}
              value={loading ? '—' : totalSims}
              to="/admin/simulations"
            />
            <StatTile
              icon={Users}
              tone="info"
              label={t('dashboard.manager.salespeople', 'Miembros')}
              value={loading ? '—' : totalSalespeople}
              hint={totalTeams ? t('dashboard.manager.teamsHint', '{{n}} equipos', { n: totalTeams }) : undefined}
              to="/team"
            />
            <StatTile
              icon={Activity}
              tone="success"
              label={t('dashboard.manager.teamSessions', 'Sesiones')}
              value={loading ? '—' : teamSessionsCount}
              to="/admin/ranking"
            />
            <StatTile
              icon={Sparkles}
              tone="warning"
              label={t('dashboard.manager.avgScore', 'Score medio')}
              value={loading ? '—' : avgScore ? toScore99(avgScore) : '—'}
              to="/admin/ranking"
            />
          </SectionCard>

          <SimulationsCard
            simulations={data?.simulations ?? []}
            loading={loading}
            createHref="/admin/simulations/new"
          />
        </div>
      </div>
    </div>
  );
}
